import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, UserPlus, UserMinus, Settings } from 'lucide-react';
import { wpApi } from '../services/wordpressApi';
import type { WPUser } from '../services/wordpressApi';
import Avatar from './layout/Avatar';
import { isNativeApp } from '../lib/isNativeApp';

interface UserProfileProps {
  userId: number;
  currentUserId: number | null;
  onBack: () => void;
  onLoginRequired: () => void;
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#09090b',
    paddingTop: '70px',
  },
  containerNative: {
    minHeight: '100vh',
    background: '#09090b',
    paddingTop: '16px',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '60px 20px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '2px solid #27272a',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  error: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    padding: '60px 20px',
    textAlign: 'center' as const,
  },
  errorIcon: {
    fontSize: '48px',
  },
  errorText: {
    fontSize: '14px',
    color: '#71717a',
  },
  backBtn: {
    position: 'absolute' as const,
    top: '80px',
    left: '16px',
    width: '40px',
    height: '40px',
    background: '#27272a',
    border: 'none',
    borderRadius: '12px',
    color: '#fafafa',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    transition: 'all 0.2s',
  },
  header: {
    position: 'relative' as const,
    paddingTop: '60px',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
  },
  avatar: {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    border: '4px solid #27272a',
    marginBottom: '16px',
    overflow: 'hidden',
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
  },
  avatarFallback: {
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '36px',
    fontWeight: 700,
    color: '#fff',
  },
  name: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#fafafa',
    margin: '0 0 8px 0',
  },
  bio: {
    fontSize: '14px',
    color: '#a1a1aa',
    textAlign: 'center' as const,
    maxWidth: '300px',
    margin: 0,
    lineHeight: 1.5,
  },
  stats: {
    display: 'flex',
    justifyContent: 'center',
    gap: '32px',
    padding: '20px',
    marginTop: '20px',
  },
  stat: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '4px',
    cursor: 'default',
    padding: '12px 20px',
    background: '#27272a',
    borderRadius: '12px',
    transition: 'all 0.2s',
  },
  statValue: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#fafafa',
  },
  statLabel: {
    fontSize: '12px',
    color: '#71717a',
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    padding: '0 20px 20px',
  },
  actionBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 24px',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    transition: 'all 0.2s',
  },
  followBtn: {
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
  },
  followingBtn: {
    background: '#27272a',
    color: '#a1a1aa',
    border: '1px solid #3f3f46',
  },
  settingsBtn: {
    background: '#27272a',
    color: '#fafafa',
    border: '1px solid #3f3f46',
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #27272a',
    padding: '0 20px',
  },
  tab: {
    flex: 1,
    padding: '14px 8px',
    background: 'none',
    border: 'none',
    color: '#71717a',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    marginBottom: '-1px',
    transition: 'all 0.2s',
  },
  tabActive: {
    color: '#3b82f6',
    borderBottomColor: '#3b82f6',
  },
  content: {
    padding: '20px',
    minHeight: '200px',
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '150px',
    color: '#71717a',
    fontSize: '14px',
  },
  userList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  userItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    background: '#18181b',
    borderRadius: '12px',
    border: '1px solid #27272a',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  userName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#fafafa',
  },
  userBio: {
    fontSize: '12px',
    color: '#71717a',
  },
};

export default function UserProfile({ userId, currentUserId, onBack, onLoginRequired }: UserProfileProps) {
  const containerStyle = isNativeApp() ? styles.containerNative : styles.container;

  const [user, setUser] = useState<WPUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [followers, setFollowers] = useState<WPUser[]>([]);
  const [following, setFollowing] = useState<WPUser[]>([]);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'posts' | 'followers' | 'following'>('posts');

  useEffect(() => {
    loadUserProfile();
  }, [userId]);

  const loadUserProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const [userData, followersData, followingData] = await Promise.all([
        wpApi.getUserProfile(userId),
        wpApi.getFollowers(userId),
        wpApi.getFollowing(userId),
      ]);
      
      setUser(userData);
      setFollowers(followersData.followers);
      setFollowing(followingData.following);
      setFollowersCount(followersData.count);
      setFollowingCount(followingData.count);

      if (currentUserId) {
        try {
          const rel = await wpApi.getRelationship(userId);
          setIsFollowing(rel.is_following);
        } catch {
          // Not authenticated
        }
      }
    } catch (err) {
      setError('Không thể tải thông tin người dùng');
    } finally {
      setLoading(false);
    }
  };

  const handleFollowToggle = async () => {
    if (!currentUserId) {
      onLoginRequired();
      return;
    }

    setActionLoading(true);
    try {
      if (isFollowing) {
        await wpApi.unfollowUser(userId);
        setIsFollowing(false);
        setFollowersCount(prev => prev - 1);
      } else {
        await wpApi.followUser(userId);
        setIsFollowing(true);
        setFollowersCount(prev => prev + 1);
      }
      const rel = await wpApi.getRelationship(userId);
    } catch (err) {
      console.error('Follow toggle failed:', err);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <button style={styles.backBtn} onClick={onBack}>
          <ArrowLeft size={20} />
        </button>
        <div style={styles.loading}>
          <div style={styles.spinner} />
          <span style={styles.errorText}>Đang tải...</span>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div style={containerStyle}>
        <button style={styles.backBtn} onClick={onBack}>
          <ArrowLeft size={20} />
        </button>
        <div style={styles.error}>
          <span style={styles.errorIcon}>😕</span>
          <p style={styles.errorText}>{error || 'Không tìm thấy người dùng'}</p>
          <button
            style={{ ...styles.actionBtn, ...styles.settingsBtn }}
            onClick={onBack}
          >
            Quay lại
          </button>
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUserId === userId;

  return (
    <motion.div
      style={containerStyle}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      
      <button 
        style={styles.backBtn}
        onClick={onBack}
        onMouseEnter={(e) => e.currentTarget.style.background = '#3f3f46'}
        onMouseLeave={(e) => e.currentTarget.style.background = '#27272a'}
      >
        <ArrowLeft size={20} />
      </button>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.avatar}>
          {user.photoURL ? (
            <img src={user.photoURL} alt={user.displayName} style={styles.avatarImg} />
          ) : (
            <div style={styles.avatarFallback}>
              {user.displayName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <h1 style={styles.name}>{user.displayName}</h1>
        {user.bio && (
          <p style={styles.bio}>{user.bio}</p>
        )}
      </div>

      {/* Stats */}
      <div style={styles.stats}>
        <div style={styles.stat}>
          <span style={styles.statValue}>{user.postsCount || 0}</span>
          <span style={styles.statLabel}>Bài viết</span>
        </div>
        <button 
          style={{ ...styles.stat, cursor: 'pointer' }}
          onClick={() => setActiveTab('followers')}
          onMouseEnter={(e) => e.currentTarget.style.background = '#3f3f46'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#27272a'}
        >
          <span style={styles.statValue}>{followersCount}</span>
          <span style={styles.statLabel}>Người theo dõi</span>
        </button>
        <button 
          style={{ ...styles.stat, cursor: 'pointer' }}
          onClick={() => setActiveTab('following')}
          onMouseEnter={(e) => e.currentTarget.style.background = '#3f3f46'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#27272a'}
        >
          <span style={styles.statValue}>{followingCount}</span>
          <span style={styles.statLabel}>Đang theo dõi</span>
        </button>
      </div>

      {/* Actions */}
      <div style={styles.actions}>
        {isOwnProfile ? (
          <motion.button
            style={{ ...styles.actionBtn, ...styles.settingsBtn }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Settings size={18} />
            Chỉnh sửa
          </motion.button>
        ) : (
          <motion.button
            style={{
              ...styles.actionBtn,
              ...(isFollowing ? styles.followingBtn : styles.followBtn),
            }}
            onClick={handleFollowToggle}
            disabled={actionLoading}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onMouseEnter={(e) => {
              if (isFollowing) {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                e.currentTarget.style.borderColor = '#ef4444';
                e.currentTarget.style.color = '#ef4444';
              }
            }}
            onMouseLeave={(e) => {
              if (isFollowing) {
                e.currentTarget.style.background = '#27272a';
                e.currentTarget.style.borderColor = '#3f3f46';
                e.currentTarget.style.color = '#a1a1aa';
              }
            }}
          >
            {actionLoading ? (
              <div style={{ ...styles.spinner, width: 18, height: 18 }} />
            ) : isFollowing ? (
              <>
                <UserMinus size={18} />
                Đang theo dõi
              </>
            ) : (
              <>
                <UserPlus size={18} />
                Theo dõi
              </>
            )}
          </motion.button>
        )}
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button 
          style={{
            ...styles.tab,
            ...(activeTab === 'posts' ? styles.tabActive : {}),
          }}
          onClick={() => setActiveTab('posts')}
        >
          Bài viết
        </button>
        <button 
          style={{
            ...styles.tab,
            ...(activeTab === 'followers' ? styles.tabActive : {}),
          }}
          onClick={() => setActiveTab('followers')}
        >
          Người theo dõi
        </button>
        <button 
          style={{
            ...styles.tab,
            ...(activeTab === 'following' ? styles.tabActive : {}),
          }}
          onClick={() => setActiveTab('following')}
        >
          Đang theo dõi
        </button>
      </div>

      {/* Tab Content */}
      <div style={styles.content}>
        {activeTab === 'posts' && (
          <div style={styles.empty}>
            Chưa có bài viết nào
          </div>
        )}

        {activeTab === 'followers' && (
          <div style={styles.userList}>
            {followers.length === 0 ? (
              <div style={styles.empty}>Chưa có người theo dõi</div>
            ) : (
              followers.map((follower) => (
                <div key={follower.id} style={styles.userItem}>
                  <Avatar name={follower.displayName} photoURL={follower.photoURL} size={40} />
                  <div style={styles.userInfo}>
                    <span style={styles.userName}>{follower.displayName}</span>
                    {follower.bio && (
                      <span style={styles.userBio}>{follower.bio}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'following' && (
          <div style={styles.userList}>
            {following.length === 0 ? (
              <div style={styles.empty}>Chưa theo dõi ai</div>
            ) : (
              following.map((follow) => (
                <div key={follow.id} style={styles.userItem}>
                  <Avatar name={follow.displayName} photoURL={follow.photoURL} size={40} />
                  <div style={styles.userInfo}>
                    <span style={styles.userName}>{follow.displayName}</span>
                    {follow.bio && (
                      <span style={styles.userBio}>{follow.bio}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
